/**
 * Email Validation Utilities
 *
 * This module provides comprehensive email validation functionality
 * following RFC 5322 standards with practical constraints for the
 * BiteWise provider registration system.
 *
 * @module utils/validation/emailValidation
 */

import type { ValidationResult } from '@/types/registration';

/**
 * RFC 5322 compliant email regex pattern
 * This pattern validates most common email formats while being practical
 * for real-world usage in provider registration.
 */
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * Email validation configuration
 */
const EMAIL_CONFIG = {
  /** Maximum allowed email length */
  MAX_LENGTH: 254,
  /** Minimum allowed email length */
  MIN_LENGTH: 5,
  /** Maximum local part length (before @) */
  MAX_LOCAL_LENGTH: 64,
  /** Maximum domain part length (after @) */
  MAX_DOMAIN_LENGTH: 253
} as const;

/**
 * Common email validation error messages
 */
const EMAIL_ERRORS = {
  REQUIRED: 'Email address is required',
  INVALID_FORMAT: 'Please enter a valid email address',
  TOO_LONG: 'Email address is too long (maximum 254 characters)',
  TOO_SHORT: 'Email address is too short',
  LOCAL_TOO_LONG: 'Email username is too long (maximum 64 characters)',
  DOMAIN_TOO_LONG: 'Email domain is too long (maximum 253 characters)',
  INVALID_CHARACTERS: 'Email contains invalid characters',
  MULTIPLE_AT_SIGNS: 'Email cannot contain multiple @ symbols',
  STARTS_WITH_DOT: 'Email cannot start with a dot',
  ENDS_WITH_DOT: 'Email cannot end with a dot',
  CONSECUTIVE_DOTS: 'Email cannot contain consecutive dots',
  DOMAIN_STARTS_WITH_DOT: 'Email domain cannot start with a dot',
  DOMAIN_ENDS_WITH_DOT: 'Email domain cannot end with a dot'
} as const;

/**
 * Validates email address format and constraints
 *
 * @param email - The email address to validate
 * @returns ValidationResult with isValid flag and error message if invalid
 *
 * @example
 * ```typescript
 * const result = validateEmail('user@example.com');
 * if (result.isValid) {
 *   logger.log('Email is valid');
 * } else {
 *   logger.log('Error:', result.error);
 * }
 * ```
 */
export const validateEmail = (email: string): ValidationResult => {
  // Check if email is provided
  if (!email || email.trim().length === 0) {
    return {
      isValid: false,
      error: EMAIL_ERRORS.REQUIRED
    };
  }

  const trimmedEmail = email.trim();

  // Check length constraints
  if (trimmedEmail.length < EMAIL_CONFIG.MIN_LENGTH) {
    return {
      isValid: false,
      error: EMAIL_ERRORS.TOO_SHORT
    };
  }

  if (trimmedEmail.length > EMAIL_CONFIG.MAX_LENGTH) {
    return {
      isValid: false,
      error: EMAIL_ERRORS.TOO_LONG
    };
  }

  // Check for multiple @ symbols
  const atCount = (trimmedEmail.match(/@/g) || []).length;
  if (atCount !== 1) {
    return {
      isValid: false,
      error: atCount === 0 ? EMAIL_ERRORS.INVALID_FORMAT : EMAIL_ERRORS.MULTIPLE_AT_SIGNS
    };
  }

  // Split into local and domain parts
  const [localPart, domainPart] = trimmedEmail.split('@');

  // Validate local part (before @)
  const localValidation = validateLocalPart(localPart);
  if (!localValidation.isValid) {
    return localValidation;
  }

  // Validate domain part (after @)
  const domainValidation = validateDomainPart(domainPart);
  if (!domainValidation.isValid) {
    return domainValidation;
  }

  // Final regex validation
  if (!EMAIL_REGEX.test(trimmedEmail)) {
    return {
      isValid: false,
      error: EMAIL_ERRORS.INVALID_FORMAT
    };
  }

  return { isValid: true };
};

/**
 * Validates the local part of an email address (before @)
 *
 * @param localPart - The local part to validate
 * @returns ValidationResult
 */
const validateLocalPart = (localPart: string): ValidationResult => {
  if (!localPart || localPart.length === 0) {
    return {
      isValid: false,
      error: EMAIL_ERRORS.INVALID_FORMAT
    };
  }

  if (localPart.length > EMAIL_CONFIG.MAX_LOCAL_LENGTH) {
    return {
      isValid: false,
      error: EMAIL_ERRORS.LOCAL_TOO_LONG
    };
  }

  // Check for invalid starting/ending characters
  if (localPart.startsWith('.') || localPart.endsWith('.')) {
    return {
      isValid: false,
      error: localPart.startsWith('.') ? EMAIL_ERRORS.STARTS_WITH_DOT : EMAIL_ERRORS.ENDS_WITH_DOT
    };
  }

  // Check for consecutive dots
  if (localPart.includes('..')) {
    return {
      isValid: false,
      error: EMAIL_ERRORS.CONSECUTIVE_DOTS
    };
  }

  return { isValid: true };
};

/**
 * Validates the domain part of an email address (after @)
 *
 * @param domainPart - The domain part to validate
 * @returns ValidationResult
 */
const validateDomainPart = (domainPart: string): ValidationResult => {
  if (!domainPart || domainPart.length === 0) {
    return {
      isValid: false,
      error: EMAIL_ERRORS.INVALID_FORMAT
    };
  }

  if (domainPart.length > EMAIL_CONFIG.MAX_DOMAIN_LENGTH) {
    return {
      isValid: false,
      error: EMAIL_ERRORS.DOMAIN_TOO_LONG
    };
  }

  // Check for invalid starting/ending characters
  if (domainPart.startsWith('.') || domainPart.endsWith('.')) {
    return {
      isValid: false,
      error: domainPart.startsWith('.') ? EMAIL_ERRORS.DOMAIN_STARTS_WITH_DOT : EMAIL_ERRORS.DOMAIN_ENDS_WITH_DOT
    };
  }

  // Check for consecutive dots
  if (domainPart.includes('..')) {
    return {
      isValid: false,
      error: EMAIL_ERRORS.CONSECUTIVE_DOTS
    };
  }

  // Domain must contain at least one dot
  if (!domainPart.includes('.')) {
    return {
      isValid: false,
      error: EMAIL_ERRORS.INVALID_FORMAT
    };
  }

  return { isValid: true };
};

/**
 * Checks if an email address is from a common provider
 * Useful for providing user-friendly warnings about typos
 *
 * @param email - The email address to check
 * @returns Object with provider info and suggestions
 */
export const getEmailProviderInfo = (email: string) => {
  const commonProviders = [
    'gmail.com',
    'yahoo.com',
    'hotmail.com',
    'outlook.com',
    'icloud.com',
    'aol.com',
    'live.com',
    'msn.com'
  ];

  const domain = email.split('@')[1]?.toLowerCase();

  if (!domain) {
    return { isCommon: false, suggestions: [] };
  }

  const isCommon = commonProviders.includes(domain);

  // Generate suggestions for potential typos
  const suggestions = commonProviders
    .filter(provider => {
      // Simple Levenshtein distance check for typos
      return getLevenshteinDistance(domain, provider) <= 2 && domain !== provider;
    })
    .slice(0, 3); // Limit to 3 suggestions

  return {
    isCommon,
    domain,
    suggestions
  };
};

/**
 * Calculates Levenshtein distance between two strings
 * Used for email domain typo detection
 *
 * @param str1 - First string
 * @param str2 - Second string
 * @returns Distance between strings
 */
const getLevenshteinDistance = (str1: string, str2: string): number => {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));

  for (let i = 0; i <= str1.length; i++) {
    matrix[0][i] = i;
  }

  for (let j = 0; j <= str2.length; j++) {
    matrix[j][0] = j;
  }

  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1, // deletion
        matrix[j - 1][i] + 1, // insertion
        matrix[j - 1][i - 1] + indicator // substitution
      );
    }
  }

  return matrix[str2.length][str1.length];
};

/**
 * Normalizes email address for consistent storage and comparison
 *
 * @param email - The email address to normalize
 * @returns Normalized email address
 */
export const normalizeEmail = (email: string): string => {
  return email.trim().toLowerCase();
};